import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Camera, CheckCircle2, Image as ImageIcon, RefreshCw, Search, Trash2, X, XCircle } from "lucide-react";
import Select from "react-select";
import { useSearchParams } from "react-router-dom";
import axiosInstance from "../../services/axiosInstance";
import {
    approveWasteReport,
    createWasteReport,
    fetchWasteReportConfig,
    fetchWasteReports,
    rejectWasteReport,
} from "../../services/wasteReportServices";
import { socket } from "../../services/socket";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import { resolveApiUrl } from "../../utils/apiBaseUrl";

const REASONS = [
    { value: "EXPIRED", label: "Expired" },
    { value: "DAMAGED", label: "Damaged" },
    { value: "SPOILED", label: "Spoiled" },
    { value: "WASTE", label: "Waste" },
    { value: "LOST", label: "Lost" },
    { value: "STORE_USE", label: "Store Use" },
    { value: "CORRECTION", label: "Correction" },
    { value: "OTHER", label: "Other" },
];

const statusStyles = {
    PENDING: "bg-amber-50 text-amber-700 ring-amber-200",
    APPROVED: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    REJECTED: "bg-rose-50 text-rose-700 ring-rose-200",
};

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const expiryLabel = (days) => {
    if (days === null || days === undefined) return "No image";
    if (days <= 0) return "Deleting soon";
    if (days === 1) return "1 day left";
    return `${days} days left`;
};

const uploadsBaseURL = resolveApiUrl("/uploads");

const resolveProofImageUrl = (report) => {
    if (report?.proof_image_path) {
        return `${uploadsBaseURL}/${String(report.proof_image_path).replace(/^\/?uploads\//, "")}`;
    }
    return report?.proof_image_url || null;
};

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 48,
        borderRadius: 16,
        borderColor: state.isFocused ? "var(--color-primary)" : "#e2e8f0",
        boxShadow: state.isFocused ? "0 0 0 4px rgba(148, 163, 184, 0.14)" : "none",
        fontSize: 14,
        fontWeight: 600,
        ":hover": {
            borderColor: state.isFocused ? "var(--color-primary)" : "#cbd5e1",
        },
    }),
    menu: (base) => ({
        ...base,
        zIndex: 80,
        borderRadius: 16,
        overflow: "hidden",
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? "var(--color-primary)"
            : state.isFocused
                ? "#f1f5f9"
                : "#fff",
        color: state.isSelected ? "#fff" : "#0f172a",
        fontSize: 14,
        fontWeight: 600,
    }),
};

export default function WasteReportsPage({ mode = "staff" }) {
    const isAdmin = mode === "admin";
    const [reports, setReports] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [statusFilter, setStatusFilter] = useState("");
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [proofRetentionMonths, setProofRetentionMonths] = useState(6);
    const [previewUrl, setPreviewUrl] = useState("");
    const [focusedReportId, setFocusedReportId] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [form, setForm] = useState({
        product_id: "",
        quantity: "1",
        reason: "EXPIRED",
        note: "",
        image: null,
    });

    const selectedProduct = useMemo(
        () => products.find((product) => String(product.id) === String(form.product_id)),
        [products, form.product_id]
    );
    const productOptions = useMemo(() => products
        .filter((product) => Number(product.stock || 0) > 0)
        .map((product) => ({
            value: String(product.id),
            label: product.name,
            barcode: product.barcode || product.product_code || "",
            product,
        })), [products]);
    const selectedProductOption = useMemo(
        () => productOptions.find((option) => option.value === String(form.product_id)) || null,
        [form.product_id, productOptions]
    );
    const selectedReasonOption = useMemo(
        () => REASONS.find((reason) => reason.value === form.reason) || null,
        [form.reason]
    );
    const statusOptions = useMemo(() => [
        { value: "", label: "All Status" },
        { value: "PENDING", label: "Pending" },
        { value: "APPROVED", label: "Approved" },
        { value: "REJECTED", label: "Rejected" },
    ], []);
    const selectedStatusOption = useMemo(
        () => statusOptions.find((option) => option.value === statusFilter) || statusOptions[0],
        [statusFilter, statusOptions]
    );

    const filteredReports = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return reports;
        return reports.filter((report) => [
            report.product_name,
            report.branch_name,
            report.reason,
            report.status,
            report.reported_by,
        ].some((value) => String(value || "").toLowerCase().includes(query)));
    }, [reports, search]);
    const reportsPerPage = 10;
    const totalPages = Math.max(1, Math.ceil(filteredReports.length / reportsPerPage));
    const safePage = Math.min(currentPage, totalPages);
    const pageStart = (safePage - 1) * reportsPerPage;
    const paginatedReports = filteredReports.slice(pageStart, pageStart + reportsPerPage);

    useEffect(() => {
        setCurrentPage(1);
    }, [search, statusFilter]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    useEffect(() => {
        if (!focusedReportId) return;
        const focusedIndex = filteredReports.findIndex((report) => String(report.id) === String(focusedReportId));
        if (focusedIndex >= 0) {
            setCurrentPage(Math.floor(focusedIndex / reportsPerPage) + 1);
        }
    }, [filteredReports, focusedReportId]);

    const loadReports = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchWasteReports(statusFilter);
            setReports(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            showErrorToast(error.response?.data?.message || "Failed to load waste reports.");
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    const loadProducts = useCallback(async () => {
        if (isAdmin) return;
        try {
            const response = await axiosInstance.get("/pos/products");
            setProducts(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            showErrorToast("Failed to load products.");
        }
    }, [isAdmin]);

    useEffect(() => {
        loadReports();
    }, [loadReports]);

    useEffect(() => {
        loadProducts();
    }, [loadProducts]);

    useEffect(() => {
        fetchWasteReportConfig()
            .then((response) => {
                const months = Number(response.data?.proof_retention_months);
                if (Number.isFinite(months) && months > 0) {
                    setProofRetentionMonths(months);
                }
            })
            .catch(() => undefined);
    }, []);

    useEffect(() => {
        const handleWasteSync = () => {
            loadReports();
            if (!isAdmin) {
                loadProducts();
            }
        };

        socket.on("waste_report_new", handleWasteSync);
        socket.on("waste_report_updated", handleWasteSync);

        return () => {
            socket.off("waste_report_new", handleWasteSync);
            socket.off("waste_report_updated", handleWasteSync);
        };
    }, [isAdmin, loadProducts, loadReports]);

    useEffect(() => {
        const focusId = searchParams.get("focus");
        if (!focusId) return;

        setFocusedReportId(focusId);
        setStatusFilter("");
        window.requestAnimationFrame(() => {
            document.getElementById(`waste-report-${focusId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        });

        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("focus");
        setSearchParams(nextParams, { replace: true });
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        if (!focusedReportId || reports.length === 0) return;
        window.requestAnimationFrame(() => {
            document.getElementById(`waste-report-${focusedReportId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        });
    }, [focusedReportId, reports]);

    const resetForm = () => {
        setForm({ product_id: "", quantity: "1", reason: "EXPIRED", note: "", image: null });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!form.product_id) return showErrorToast("Select a product.");
        if (!Number(form.quantity) || Number(form.quantity) <= 0) return showErrorToast("Enter a valid quantity.");

        const body = new FormData();
        body.append("product_id", form.product_id);
        body.append("quantity", form.quantity);
        body.append("reason", form.reason);
        body.append("note", form.note);
        if (form.image) body.append("image", form.image);

        setSaving(true);
        try {
            const response = await createWasteReport(body);
            const createdReport = response.data?.report;
            if (createdReport && (!statusFilter || createdReport.status === statusFilter)) {
                setReports((prev) => [{
                    ...createdReport,
                    product_name: createdReport.product_name || selectedProduct?.name || null,
                    product_image_url: createdReport.product_image_url || selectedProduct?.image || null,
                }, ...prev]);
            }
            showSuccessToast(response.data?.message || "Waste report submitted.");
            resetForm();
            await loadReports();
        } catch (error) {
            showErrorToast(error.response?.data?.message || "Failed to submit waste report.");
        } finally {
            setSaving(false);
        }
    };

    const handleReview = async (id, action) => {
        try {
            const response = action === "approve"
                ? await approveWasteReport(id)
                : await rejectWasteReport(id);
            showSuccessToast(response.data?.message || "Waste report updated.");
            loadReports();
        } catch (error) {
            showErrorToast(error.response?.data?.message || "Failed to review waste report.");
        }
    };

    return (
        <div className="flex w-full max-w-full min-w-0 flex-col gap-4 pt-3">
            <section className="shrink-0 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
                            <Trash2 size={14} />
                            {isAdmin ? "Waste Review" : "Waste Reporting"}
                        </div>
                        <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-950">
                            {isAdmin ? "Waste Requests" : "Waste Reports"}
                        </h1>
                        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                            {isAdmin
                                ? "Review staff reports for expired, damaged, spoiled, or missing items before stock is deducted."
                                : "Report items that can no longer be sold. Inventory changes only after admin approval."}
                        </p>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <label className="relative block">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                            <input
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Search reports..."
                                className="h-11 w-full rounded-2xl border border-slate-200 bg-white pl-10 pr-4 text-sm outline-none focus:border-[var(--color-primary)] focus:ring-4 focus:ring-slate-100 sm:w-64"
                            />
                        </label>
                        <div className="min-w-[180px]">
                            <Select
                                value={selectedStatusOption}
                                onChange={(option) => setStatusFilter(option?.value || "")}
                                options={statusOptions}
                                isSearchable
                                styles={selectStyles}
                                placeholder="All Status"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={loadReports}
                            className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                        >
                            <RefreshCw size={16} />
                            Refresh
                        </button>
                    </div>
                </div>
            </section>

            {!isAdmin && (
                <form onSubmit={handleSubmit} className="w-full max-w-full shrink-0 rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                    <div className="grid gap-4 lg:grid-cols-[1.3fr,120px,160px]">
                        <label className="block">
                            <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Product</span>
                            <div className="mt-2">
                                <Select
                                    value={selectedProductOption}
                                    onChange={(option) => setForm((prev) => ({ ...prev, product_id: option?.value || "" }))}
                                    options={productOptions}
                                    isSearchable
                                    isClearable
                                    filterOption={(option, inputValue) => {
                                        const query = String(inputValue || "").trim().toLowerCase();
                                        if (!query) return true;
                                        const product = option.data.product || {};
                                        return [
                                            product.name,
                                            product.barcode,
                                            product.product_code,
                                            option.data.barcode,
                                        ].some((value) => String(value || "").toLowerCase().includes(query));
                                    }}
                                    formatOptionLabel={(option) => (
                                        <div className="min-w-0">
                                            <div className="truncate font-semibold text-slate-900">{option.product?.name || option.label}</div>
                                            <div className="mt-0.5 flex flex-wrap gap-2 text-[11px] font-semibold text-slate-500">
                                                <span>{option.product?.stock || 0} in branch</span>
                                                {option.barcode && <span>Barcode: {option.barcode}</span>}
                                            </div>
                                        </div>
                                    )}
                                    placeholder="Search product..."
                                    styles={selectStyles}
                                    noOptionsMessage={() => "No products found"}
                                />
                            </div>
                        </label>
                        <label className="block">
                            <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Qty</span>
                            <input
                                type="number"
                                min="1"
                                value={form.quantity}
                                onChange={(event) => setForm((prev) => ({ ...prev, quantity: event.target.value }))}
                                className="mt-2 h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm font-semibold outline-none focus:border-[var(--color-primary)]"
                            />
                        </label>
                        <label className="block">
                            <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Reason</span>
                            <div className="mt-2">
                                <Select
                                    value={selectedReasonOption}
                                    onChange={(option) => setForm((prev) => ({ ...prev, reason: option?.value || "EXPIRED" }))}
                                    options={REASONS}
                                    isSearchable
                                    styles={selectStyles}
                                    placeholder="Select reason..."
                                />
                            </div>
                        </label>
                    </div>
                    <div className="mt-4 grid gap-4 lg:grid-cols-[1fr,240px]">
                        <label className="block">
                            <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Note</span>
                            <textarea
                                value={form.note}
                                onChange={(event) => setForm((prev) => ({ ...prev, note: event.target.value }))}
                                placeholder="Optional details for admin review"
                                className="mt-2 min-h-[96px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[var(--color-primary)]"
                            />
                        </label>
                        <label className="block">
                            <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Proof Image</span>
                            <div className="mt-2 flex min-h-[96px] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 text-center">
                                <Camera className="h-6 w-6 text-slate-400" />
                                <span className="mt-2 text-xs font-semibold text-slate-600">
                                    {form.image ? form.image.name : "Optional image"}
                                </span>
                                <span className="mt-1 text-[11px] text-slate-400">
                                    Saved as WebP, expires in {proofRetentionMonths} {proofRetentionMonths === 1 ? "month" : "months"}
                                </span>
                            </div>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(event) => setForm((prev) => ({ ...prev, image: event.target.files?.[0] || null }))}
                                className="sr-only"
                            />
                        </label>
                    </div>
                    {selectedProduct && (
                        <p className="mt-3 text-xs font-semibold text-slate-500">
                            Current branch stock: {selectedProduct.stock || 0}
                        </p>
                    )}
                    <button
                        type="submit"
                        disabled={saving}
                        className="mt-4 inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl px-5 text-sm font-bold text-white shadow-sm transition hover:opacity-90 disabled:opacity-60 sm:w-auto"
                        style={{ background: "var(--color-primary)" }}
                    >
                        <AlertTriangle size={17} />
                        {saving ? "Submitting..." : "Submit Waste Report"}
                    </button>
                </form>
            )}

            <section className="min-h-[260px] w-full max-w-full shrink-0 overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
                {loading ? (
                    <div className="flex h-64 items-center justify-center text-sm font-semibold text-slate-500">Loading waste reports...</div>
                ) : filteredReports.length === 0 ? (
                    <div className="flex h-64 flex-col items-center justify-center text-center">
                        <Trash2 className="h-10 w-10 text-slate-300" />
                        <p className="mt-3 font-semibold text-slate-900">No waste reports</p>
                        <p className="mt-1 text-sm text-slate-500">Reports matching your filters will appear here.</p>
                    </div>
                ) : (
                    <>
                    <div className="w-full max-w-full space-y-3 p-2 sm:p-3 lg:space-y-0 lg:divide-y lg:divide-slate-100 lg:p-0">
                        {paginatedReports.map((report, index) => (
                            <article
                                key={report.id}
                                id={`waste-report-${report.id}`}
                                className={`grid min-w-0 max-w-full gap-4 rounded-2xl border border-slate-100 bg-white p-3 transition sm:p-4 lg:grid-cols-[1.4fr,1fr,170px] lg:items-center lg:rounded-none lg:border-0 ${String(focusedReportId) === String(report.id) ? "bg-amber-50/70 ring-2 ring-amber-200" : ""}`}
                            >
                                <div className="flex min-w-0 flex-col gap-3 sm:flex-row">
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-600 shadow-sm">
                                        {pageStart + index + 1}
                                    </span>
                                    {(() => {
                                        const proofUrl = resolveProofImageUrl(report);

                                        return (
                                    <button
                                        type="button"
                                        onClick={() => proofUrl && setPreviewUrl(proofUrl)}
                                        disabled={!proofUrl}
                                        className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 disabled:cursor-default"
                                        title={proofUrl ? "View proof image" : "No proof image"}
                                    >
                                        {proofUrl ? (
                                            <img src={proofUrl} alt="Waste proof" className="h-full w-full object-cover" />
                                        ) : (
                                            <ImageIcon className="h-6 w-6 text-slate-300" />
                                        )}
                                    </button>
                                        );
                                    })()}
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <h3 className="min-w-0 max-w-full break-words text-sm font-bold text-slate-950">{report.product_name || `Product #${report.product_id}`}</h3>
                                            <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${statusStyles[report.status] || "bg-slate-100 text-slate-600 ring-slate-200"}`}>
                                                {report.status}
                                            </span>
                                        </div>
                                        <p className="mt-1 text-xs font-semibold text-slate-500">
                                            {report.branch_name || "Branch"} | Qty {report.quantity} | {REASONS.find((reason) => reason.value === report.reason)?.label || report.reason}
                                        </p>
                                        {report.note && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{report.note}</p>}
                                    </div>
                                </div>
                                <div className="grid min-w-0 gap-3 text-xs text-slate-500 sm:grid-cols-2 lg:grid-cols-1">
                                    <div>
                                        <span className="font-bold text-slate-400">Reported by</span>
                                        <p className="font-semibold text-slate-700">{report.reported_by || "-"}</p>
                                        <p>{formatDate(report.createdAt)}</p>
                                    </div>
                                    <div>
                                        <span className="font-bold text-slate-400">Proof retention</span>
                                        <p className={`mt-1 inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ${resolveProofImageUrl(report) ? "bg-sky-50 text-sky-700" : "bg-slate-100 text-slate-500"}`}>
                                            {expiryLabel(report.proof_days_remaining)}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex min-w-0 flex-wrap justify-start gap-2 lg:justify-end">
                                    {isAdmin && report.status === "PENDING" ? (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => handleReview(report.id, "approve")}
                                                className="inline-flex h-10 w-28 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-3 text-xs font-bold text-white shadow-sm transition hover:bg-emerald-700"
                                            >
                                                <CheckCircle2 size={15} />
                                                Approve
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleReview(report.id, "reject")}
                                                className="inline-flex h-10 w-28 items-center justify-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 text-xs font-bold text-rose-700 transition hover:bg-rose-100"
                                            >
                                                <XCircle size={15} />
                                                Reject
                                            </button>
                                        </>
                                    ) : (
                                        <span className="text-xs font-semibold text-slate-500">
                                            {report.reviewed_by ? `Reviewed by ${report.reviewed_by}` : "Awaiting review"}
                                        </span>
                                    )}
                                </div>
                            </article>
                        ))}
                    </div>
                    <div className="flex items-center justify-between gap-3 overflow-x-auto border-t border-slate-100 px-4 py-3 text-xs whitespace-nowrap sm:px-5">
                        <div className="shrink-0 text-slate-500">
                            Showing {pageStart + 1}-{Math.min(pageStart + reportsPerPage, filteredReports.length)} of {filteredReports.length}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                            <button
                                type="button"
                                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                                disabled={safePage === 1}
                            >
                                Prev
                            </button>
                            <div className="text-xs font-semibold text-slate-600">Page {safePage} of {totalPages}</div>
                            <button
                                type="button"
                                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                                disabled={safePage === totalPages}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                    </>
                )}
            </section>

            {previewUrl && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm" onClick={() => setPreviewUrl("")}>
                    <button
                        type="button"
                        onClick={() => setPreviewUrl("")}
                        className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-600 shadow-lg"
                        aria-label="Close image preview"
                    >
                        <X size={20} />
                    </button>
                    <img
                        src={previewUrl}
                        alt="Waste proof preview"
                        className="max-h-[86vh] max-w-[92vw] rounded-2xl bg-white object-contain shadow-2xl"
                        onClick={(event) => event.stopPropagation()}
                    />
                </div>
            )}
        </div>
    );
}
